import omit from 'lodash/omit';

// @ts-strict-ignore
import {Metric, MetricsRequest} from 'app_api/projects/metrics/types';
import {BehaviorEntity} from 'app_api/projects/behaviors/types';
import {useMetrics} from 'common/contexts/react/metrics-resource-context';
import {MultiMetricsSectionItem} from 'common/query-builder/query-block/multi-metrics-menu/types';
import {isFormulaShowClause} from 'common/report/insights/models/utils';
import {EntityType} from 'common/report/util/saved-entities/types';
import * as BookmarkTypes from 'common/types/reports/bookmark';
import {BehaviorsRequest} from 'app_api/projects/behaviors/types';
import {useBehaviors} from 'common/contexts/react/behaviors-context';

import {ShowClause} from '../util/types';
import {getUpdatedFormulaAndMetrics} from './formula-utils';
import {
  getClauseFromBehaviorModel,
  getClauseFromMetricModel,
  getMetricAtIndex,
  getShowClauseFromMetricItemSelection,
  updateMetricOrReferencedMetric,
} from './helpers';
import {useReportBookmark} from './report-bookmark-manager';
import {attemptRequest, useSaveAndShareFlow} from './save-utils';

export function useMetricEntries() {
  const metrics = useMetrics();
  const behaviors = useBehaviors();
  const {params: bookmarkParams, updateParams} = useReportBookmark();

  const saveAndShareMetric = useSaveAndShareFlow<Metric, MetricsRequest>({
    entityType: EntityType.Metric,
    existingNamesFn: () => metrics.getAllValues().map((metric) => metric.name),
    createFn: (entity) => metrics.create(entity),
  });

  const saveAndShareBehavior = useSaveAndShareFlow<BehaviorEntity, BehaviorsRequest>({
    entityType: EntityType.Behavior,
    existingNamesFn: () => behaviors.getAllValues().map((behavior) => behavior.name),
    createFn: (entity) => behaviors.create(entity),
  });

  /**
   * Updates an existing metric, clones or creates a new entity
   * This handles both behavior & formula based metrics
   */
  const saveOrCreateMetricFromClause = async ({
    clause,
    saveAsNew = false,
  }: {
    clause: ShowClause;
    saveAsNew?: boolean;
  }) => {
    let metricEntity: MetricsRequest;

    if (isFormulaShowClause(clause)) {
      // preps referenced metrics for saving
      const {definition: formulaDefinition, referencedMetrics} = getUpdatedFormulaAndMetrics({
        formula: clause,
        // when saving a formula, the metrics referenced in it can come from the main report or metrics inside the formula
        entries: clause.referencedMetrics?.length ? clause.referencedMetrics : bookmarkParams.sections.show,
      });

      metricEntity = {
        type: BookmarkTypes.TopLevelMetricType.Formula,
        name: clause.name!,
        description: ``,
        definition: {
          formula: {
            definition: formulaDefinition,
            referencedMetrics: referencedMetrics.map((metric) =>
              omit(metric, `isHidden`, `isExpanded`),
            ) as Array<BookmarkTypes.BehaviorShowClause>,
          },
          measurement: clause.measurement,
          display: clause.display,
        },
      };
    } else {
      metricEntity = {
        type: BookmarkTypes.TopLevelMetricType.Metric,
        name: clause.name!,
        description: ``,
        definition: {
          behavior: clause.behavior,
          measurement: clause.measurement,
          display: clause.display,
        },
      };
    }

    let metric: Metric | null;
    if (clause.id && !saveAsNew) {
      // when saving an existing metric, patch the model instead
      metric = await attemptRequest(() => metrics.patch(clause.id!, metricEntity));
    } else if (clause.id && saveAsNew) {
      // duplicating an existing metric will show the save & share flow
      metric = await saveAndShareMetric(metricEntity, {isDuplicating: true});
    } else {
      metric = await saveAndShareMetric(metricEntity);
    }

    if (!metric) {
      return null;
    }

    return getClauseFromMetricModel(metric);
  };

  /**
   * Updates an existing behavior in place, clones or creates a new entity
   */
  const saveOrCreateBehaviorFromClause = async ({
    clause,
    saveAsNew = false,
  }: {
    clause: ShowClause;
    saveAsNew?: boolean;
  }): Promise<ShowClause | null> => {
    if (isFormulaShowClause(clause)) {
      throw new Error(`attempted to save a behavior inside a formula.`);
    }

    const clauseBehavior = clause.behavior;

    const entity: BehaviorEntity = {
      type: clauseBehavior.type,
      name: clauseBehavior.name,
      description: ``,
      definition: {
        behavior: omit(
          clauseBehavior,
          `id`,
          `hasUnsavedChanges`,
          `isExpanded`,
          `name`,
          `unsavedId`,
          `search`,
          `projectId`,
        ),
      },
    };

    let behavior: BehaviorEntity;
    if (clauseBehavior.id && !saveAsNew) {
      // when saving an existing behavior, patch the model instead
      behavior = await attemptRequest(() => behaviors.patch(clauseBehavior.id, entity));
    } else if (clause.id && saveAsNew) {
      // duplicating an existing behavior will show the save & share flow
      behavior = await saveAndShareBehavior(entity, {isDuplicating: true});
    } else {
      behavior = await saveAndShareBehavior(entity);
    }

    if (!behavior) {
      return null;
    }

    return getClauseFromBehaviorModel({behavior, clause});
  };

  /**
   * Handles saving a a top level metric or a metric nested inside a formula
   */
  const saveMetric = async ({
    index,
    parentMetricIndex,
    saveAsNew = false,
  }: {
    index: number;
    parentMetricIndex?: number;
    saveAsNew?: boolean;
  }) => {
    const clause = getMetricAtIndex({
      index,
      parentMetricIndex,
      params: bookmarkParams,
    });

    const newClause = await saveOrCreateMetricFromClause({clause, saveAsNew});

    if (newClause) {
      updateParams((params) => {
        updateMetricOrReferencedMetric({
          index,
          parentMetricIndex,
          params,
          newClause,
        });
      });
    }
  };

  /**
   * Handles a selection from the metrics menu for a given top level metric or a metric nested inside a formula
   */
  const selectMetricItem = ({
    index,
    parentMetricIndex,
    dataGroupId,
    selectedItem,
  }: {
    index: number;
    parentMetricIndex?: number;
    dataGroupId: string | null;
    selectedItem: MultiMetricsSectionItem;
  }) => {
    const newClause = getShowClauseFromMetricItemSelection({dataGroupId, selectedItem});
    updateParams((params) => {
      updateMetricOrReferencedMetric({
        index,
        parentMetricIndex,
        params,
        newClause,
      });
    });
  };

  /**
   * Resets a top level metric or a metric nested inside a formula to the saved entity state
   */
  const resetMetric = ({index, parentMetricIndex}: {index: number; parentMetricIndex?: number}) => {
    const clause = getMetricAtIndex({index, parentMetricIndex, params: bookmarkParams});
    const model = clause.id ? metrics.getOne(clause.id) : null;

    if (!model?.value) {
      return;
    }

    updateParams((params) =>
      updateMetricOrReferencedMetric({
        index,
        parentMetricIndex,
        params,
        newClause: getClauseFromMetricModel(model.value),
      }),
    );
  };

  /**
   * Handles saving a behavior inside a metric or referenced metric in a formula
   */
  const saveBehavior = async ({
    index,
    parentMetricIndex,
    saveAsNew = false,
  }: {
    index: number;
    parentMetricIndex?: number;
    saveAsNew?: boolean;
  }) => {
    const clause = getMetricAtIndex({
      index,
      parentMetricIndex,
      type: BookmarkTypes.TopLevelMetricType.Metric,
      params: bookmarkParams,
    });

    const newClause = await saveOrCreateBehaviorFromClause({clause, saveAsNew});

    if (newClause) {
      updateParams((params) => {
        updateMetricOrReferencedMetric({
          index,
          parentMetricIndex,
          params,
          newClause,
        });
      });
    }
  };

  /**
   * Resets a saved behavior to its saved entity state
   */
  const resetBehavior = ({index, parentMetricIndex}: {index: number; parentMetricIndex?: number}) => {
    const clause = getMetricAtIndex({
      index,
      parentMetricIndex,
      type: BookmarkTypes.TopLevelMetricType.Metric,
      params: bookmarkParams,
    });

    // const clause = bookmarkParams.sections.show[index];
    const model = clause.behavior.id ? behaviors.getOne(clause.behavior.id) : null;

    if (!model?.value) {
      return;
    }

    updateParams((params) =>
      updateMetricOrReferencedMetric({
        index,
        parentMetricIndex,
        params,
        newClause: getClauseFromBehaviorModel({behavior: model.value, clause}),
      }),
    );
  };

  return {
    resetMetric,
    saveMetric,
    selectMetricItem,
    resetBehavior,
    saveBehavior,
  };
}
